import { useEffect } from "react";

/**
 * Brand-colour helpers for the staff console (iter337).
 *
 * A community picks its own accent, and the console sidebar is dark ink — so two things can
 * go wrong on their own: a pale brand under white text, and a near-black brand that vanishes
 * against the sidebar. Both are fixed here rather than left to the community to discover.
 */
const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

export const parseHex = (hex) => {
  const h = String(hex || "").trim().replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [0, 2, 4].map((i) => parseInt(full.slice(i, i + 2), 16));
};

const toHex = ([r, g, b]) =>
  "#" + [r, g, b].map((v) => clamp(v).toString(16).padStart(2, "0")).join("");

// Relative luminance (WCAG) — 0 is black, 1 is white.
export const luminance = (hex) => {
  const rgb = parseHex(hex);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const mix = (hex, target, amount) => {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return toHex(rgb.map((v, i) => v + (target[i] - v) * amount));
};

/**
 * A safe { bg, fg } pair for the selected nav item on the dark sidebar.
 * Near-black brands get lifted so the selection is visible at all; pale brands get ink text
 * instead of white so the label stays readable.
 */
export const navBrand = (hex) => {
  if (!parseHex(hex)) return null;
  let bg = hex;
  if (luminance(bg) < 0.06) bg = mix(bg, [255, 255, 255], 0.4);
  return { bg, fg: luminance(bg) > 0.5 ? "#191614" : "#ffffff" };
};

const rgbStr = (hex) => (parseHex(hex) || [211, 47, 47]).join(" ");

const toHsl = (hex) => {
  const [r, g, b] = (parseHex(hex) || [211, 47, 47]).map((v) => v / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return `0 0% ${Math.round(l * 100)}%`;
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * A full 50–900 palette from one brand colour. Tints come from the colour as picked; the 500
 * "button" shade is darkened until white text reads on it (pale brands would otherwise give
 * white-on-yellow buttons), and the darker shades step down from there.
 */
export const brandPalette = (hex) => {
  if (!parseHex(hex)) return null;
  let base = hex;
  for (let i = 0; i < 12 && luminance(base) > 0.28; i++) base = mix(base, [0, 0, 0], 0.12);
  return {
    50: mix(hex, [255, 255, 255], 0.92), 100: mix(hex, [255, 255, 255], 0.82),
    200: mix(hex, [255, 255, 255], 0.64), 300: mix(hex, [255, 255, 255], 0.45),
    400: mix(hex, [255, 255, 255], 0.22), 500: base,
    600: mix(base, [0, 0, 0], 0.15), 700: mix(base, [0, 0, 0], 0.32),
    800: mix(base, [0, 0, 0], 0.5), 900: mix(base, [0, 0, 0], 0.66),
  };
};

const THEME_VARS = ["--primary", "--ring", "--accent", "--accent-foreground", "--chart-1",
  ...[50, 100, 200, 300, 400, 500, 600, 700, 800, 900].map((k) => `--rl-${k}`)];

/**
 * Recolour the console: every `redline-*` utility, shadcn primary/ring/accent and text selection
 * follow the community's colour. Set on <html> so portalled dialogs, menus and toasts follow too.
 * Pass null/"" to restore the Redline red defaults from index.css.
 */
export const applyBrandTheme = (hex) => {
  const root = typeof document !== "undefined" ? document.documentElement : null;
  if (!root) return;
  const pal = brandPalette(hex);
  if (!pal) { THEME_VARS.forEach((v) => root.style.removeProperty(v)); root.removeAttribute("data-brand"); return; }
  Object.entries(pal).forEach(([k, v]) => root.style.setProperty(`--rl-${k}`, rgbStr(v)));
  root.style.setProperty("--primary", toHsl(pal[500]));
  root.style.setProperty("--ring", toHsl(pal[500]));
  root.style.setProperty("--accent", toHsl(pal[50]));
  root.style.setProperty("--accent-foreground", toHsl(pal[700]));
  root.style.setProperty("--chart-1", toHsl(pal[500]));
  root.setAttribute("data-brand", pal[500]);
};

/** The accent currently in force (brand or Redline red) as a hex — for charts/SVG that can't use classes. */
export const brandAccent = () => {
  const root = typeof document !== "undefined" ? document.documentElement : null;
  return (root && root.getAttribute("data-brand")) || "#D32F2F";
};

/** Theme the page with a community colour for as long as the component is mounted. */
export const useBrandTheme = (hex) => {
  useEffect(() => { applyBrandTheme(hex || ""); return () => applyBrandTheme(null); }, [hex]);
};
